import type { ApiClient } from "@notesnook/ai";

export const STARTUP_RUM_READY_EVENT = "mathgpt-startup-rum-ready";

type StartupRumPhase =
  | "html"
  | "entry"
  | "locale_loaded"
  | "root_import_start"
  | "root_import_done"
  | "start_app"
  | "root_start"
  | "desktop_titlebar_done"
  | "bootstrap_init_start"
  | "bootstrap_init_done"
  | "keystore_init_start"
  | "keystore_init_done"
  | "react_render_start"
  | "app_visible"
  | "fallback_visible"
  | "startup_error"
  | "online"
  | "offline";

interface StartupRumSnapshot {
  online?: boolean;
  visibilityState?: string;
  connectionEffectiveType?: string;
  connectionType?: string;
  connectionDownlink?: number;
  connectionRtt?: number;
}

interface StartupRumMark extends StartupRumSnapshot {
  phase: string;
  elapsedMs: number;
  fallbackShown?: boolean;
  errorName?: string;
  errorMessage?: string;
}

interface StartupResourceFailure extends StartupRumSnapshot {
  elapsedMs: number;
  resourceUrl?: string;
  resourceType?: string;
  resourceTagName?: string;
}

interface StartupRumState {
  startedAt: number;
  marks: StartupRumMark[];
  resourceFailures: StartupResourceFailure[];
  fallbackShown?: boolean;
  sentBatches?: Partial<Record<StartupRumBatchKey, boolean>>;
  resourceFailuresFlushed?: boolean;
  mark?: (phase: string, extra?: Partial<StartupRumMark>) => void;
  clearFallbackTimer?: () => void;
  stopCapture?: () => void;
}

declare global {
  interface Window {
    __MATHGPT_STARTUP_RUM__?: StartupRumState;
  }
}

type StartupRumBatchKey = "fallback_visible" | "app_visible" | "startup_error";

const MAX_FLUSH_MARKS = 32;
const MAX_RESOURCE_FAILURES = 8;
const STARTUP_DIAGNOSTIC_MAX_ELAPSED_MS = 86_400_000;

// Mirrors the inline HTML collector only for fallback paths where it did not run.
function readConnectionSnapshot(): StartupRumSnapshot {
  if (typeof navigator === "undefined") {
    return {};
  }

  const connection = (
    navigator as Navigator & {
      connection?: {
        effectiveType?: string;
        type?: string;
        downlink?: number;
        rtt?: number;
      };
    }
  ).connection;

  return {
    online: navigator.onLine,
    visibilityState:
      typeof document === "undefined" ? undefined : document.visibilityState,
    connectionEffectiveType: connection?.effectiveType,
    connectionType: connection?.type,
    connectionDownlink: connection?.downlink,
    connectionRtt: connection?.rtt,
  };
}

function elapsedSince(startedAt: number): number {
  if (typeof performance === "undefined") {
    return 0;
  }
  return Math.max(0, Math.round(performance.now() - startedAt));
}

function normalizeElapsedMs(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) {
    return 0;
  }
  return Math.min(
    STARTUP_DIAGNOSTIC_MAX_ELAPSED_MS,
    Math.max(0, Math.round(value)),
  );
}

function getStartupRumState(): StartupRumState | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (!window.__MATHGPT_STARTUP_RUM__) {
    window.__MATHGPT_STARTUP_RUM__ = {
      startedAt:
        typeof performance === "undefined" ? 0 : performance.now(),
      marks: [],
      resourceFailures: [],
    };
  }

  return window.__MATHGPT_STARTUP_RUM__;
}

function compactMessage(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return value.length > 500 ? `${value.slice(0, 497)}...` : value;
}

function appendStartupMark(
  state: StartupRumState,
  mark: StartupRumMark,
): void {
  if (state.marks.length >= MAX_FLUSH_MARKS) {
    state.marks.splice(1, 1);
  }
  state.marks.push(mark);
}

function emitStartupRumReady(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(STARTUP_RUM_READY_EVENT));
}

function hasPhase(state: StartupRumState, phase: string): boolean {
  return state.marks.some((mark) => mark.phase === phase);
}

function getNextStartupRumBatchKey(
  state: StartupRumState,
): StartupRumBatchKey | null {
  const sentBatches = state.sentBatches ?? {};

  if (hasPhase(state, "app_visible") && !sentBatches.app_visible) {
    return "app_visible";
  }

  if (hasPhase(state, "startup_error") && !sentBatches.startup_error) {
    return "startup_error";
  }

  if (
    (state.fallbackShown || hasPhase(state, "fallback_visible")) &&
    !sentBatches.fallback_visible &&
    !sentBatches.app_visible
  ) {
    return "fallback_visible";
  }

  return null;
}

function selectMarksForFlush(marks: StartupRumMark[]): StartupRumMark[] {
  if (marks.length <= MAX_FLUSH_MARKS) {
    return marks;
  }

  const headCount = 8;
  return [
    ...marks.slice(0, headCount),
    ...marks.slice(-(MAX_FLUSH_MARKS - headCount)),
  ];
}

export function markStartupPhase(
  phase: StartupRumPhase,
  extra: Partial<StartupRumMark> = {},
): void {
  const state = getStartupRumState();
  if (!state) return;
  const safeExtra = {
    ...extra,
    errorMessage: compactMessage(extra.errorMessage),
  };

  if (typeof performance !== "undefined") {
    try {
      performance.mark(`startup:${phase}`);
    } catch {
      // Browser performance marks are best-effort only.
    }
  }

  if (state.mark) {
    state.mark(phase, safeExtra);
  } else {
    appendStartupMark(state, {
      ...readConnectionSnapshot(),
      ...safeExtra,
      phase,
      elapsedMs: elapsedSince(state.startedAt),
      errorMessage: compactMessage(safeExtra.errorMessage),
    });
  }

  if (phase === "startup_error" || phase === "fallback_visible") {
    emitStartupRumReady();
  }
}

export function markAppVisible(): void {
  const state = getStartupRumState();
  state?.clearFallbackTimer?.();
  markStartupPhase("app_visible", { fallbackShown: state?.fallbackShown });
  state?.stopCapture?.();
  emitStartupRumReady();
}

export function isStartupRumReadyForFlush(): boolean {
  const state = getStartupRumState();
  if (!state) return false;
  return getNextStartupRumBatchKey(state) !== null;
}

export function flushStartupRum(
  apiClient: Pick<ApiClient, "reportStartupDiagnostics">,
  options: { anonymous?: boolean } = {},
): void {
  const state = getStartupRumState();
  if (!state) return;

  const batchKey = getNextStartupRumBatchKey(state);
  if (!batchKey) return;

  state.sentBatches = {
    ...state.sentBatches,
    [batchKey]: true,
  };

  const marks = selectMarksForFlush(state.marks);
  const resourceFailures = state.resourceFailuresFlushed
    ? []
    : state.resourceFailures.slice(0, MAX_RESOURCE_FAILURES);
  if (resourceFailures.length > 0) {
    state.resourceFailuresFlushed = true;
  }

  apiClient.reportStartupDiagnostics(
    [
      ...marks.map((mark) => ({
        phase: mark.phase,
        elapsedMs: normalizeElapsedMs(mark.elapsedMs),
        source: "startup-rum",
        online: mark.online,
        visibilityState: mark.visibilityState,
        connectionEffectiveType: mark.connectionEffectiveType,
        connectionType: mark.connectionType,
        connectionDownlink: mark.connectionDownlink,
        connectionRtt: mark.connectionRtt,
        fallbackShown: mark.fallbackShown ?? state.fallbackShown,
        errorName: mark.errorName,
        errorMessage: compactMessage(mark.errorMessage),
      })),
      ...resourceFailures.map((failure) => ({
        phase: "resource_failure",
        elapsedMs: normalizeElapsedMs(failure.elapsedMs),
        source: "startup-rum",
        online: failure.online,
        visibilityState: failure.visibilityState,
        connectionEffectiveType: failure.connectionEffectiveType,
        connectionType: failure.connectionType,
        connectionDownlink: failure.connectionDownlink,
        connectionRtt: failure.connectionRtt,
        fallbackShown: state.fallbackShown,
        resourceUrl: failure.resourceUrl,
        resourceType: failure.resourceType,
        resourceTagName: failure.resourceTagName,
      })),
    ],
    options,
  );
}
